import SDKConfiguraitonModel from "../Configurator/Models/SDKConfiguraitonModel";
import {
  initDisableCache,
  isDisabledCache,
  setBaseUrl,
} from "../Global/Helper";
import MainFiltersBQ from "../Global/Models/Filters/MainFiltersBQ";
import PotmUserVotesFilters from "../Namespaces/Voting/Models/PlayerOfTheMatch/Filters/PotmUserVotesFilters";
import PollsFilters from "../Namespaces/Voting/Models/Polls/Filters/PollsFilters";
import PlayerOfTheMatchRemapper from "../Namespaces/Voting/Models/Remappers/PlayerOfTheMatchRemapper";
import PollRemapper from "../Namespaces/Voting/Models/Remappers/PollRemapper";
import Https from "./Https";

export default class VotingHttps extends Https {
  private pollRemapper: PollRemapper = null;
  private playerOfTheMatchRemapper: PlayerOfTheMatchRemapper = null;

  constructor(config: SDKConfiguraitonModel) {
    super(config, setBaseUrl(config.environment, "voting"));
    this.pollRemapper = new PollRemapper();
    this.playerOfTheMatchRemapper = new PlayerOfTheMatchRemapper();
  }

  public getPolls = async (filters: PollsFilters, disableCache?: boolean) => {
    let url = `/polls${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructMainFiltersUrl();
    }

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = "There was a problem fetching polls";
    const response = await this.fetchWithoutAuth(url, warnMessage);

    return this.pollRemapper.remapPolls(response);
  };

  public getPollById = async (pollId: string, disableCache?: boolean) => {
    let url = `/polls/${pollId}${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage =
      "There was a problem fetching the following poll: " + pollId;
    const response = await this.fetchWithoutAuth(url, warnMessage);

    return this.pollRemapper.remapPollById(response.data);
  };

  public voteForPoll = async (pollId: string, optionIds: string[]) => {
    const url = `/polls/${pollId}/votes${this.apiSignInUrl}`;
    const method = "PUT";
    const requestBody = { option_ids: optionIds };
    const warnMessage =
      "There was a problem voting for the following poll: " + pollId;
    const response = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      requestBody
    );

    return this.pollRemapper.remapUserVote(response.data);
  };

  public deleteVoteForPoll = async (pollId: string) => {
    const url = `/polls/${pollId}/votes${this.apiSignInUrl}`;
    const method = "DELETE";
    const warnMessage =
      "There was a problem deleting vote for the following poll: " + pollId;
    await this.fetchWithAuth(url, warnMessage, method);

    return true;
  };

  public getMyPollVote = async (pollId: string) => {
    const url = `/polls/${pollId}/votes${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem fetching user's own vote for the following poll: " +
      pollId;
    const response = await this.fetchWithAuth(url, warnMessage);

    return this.pollRemapper.remapSinglePollVote(response.data);
  };

  public getMyPollsVotes = async (filters: MainFiltersBQ) => {
    let url = `/polls/votes${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructMainFiltersUrl();
    }

    const warnMessage = "There was a problem fetching user's votes for polls";
    const response = await this.fetchWithAuth(url, warnMessage);

    return this.pollRemapper.remapPollsVotes(response);
  };

  public voteForPlayerOfTheMatch = async (
    matchId: string,
    playerId: string
  ) => {
    const url = `/football/potm/${matchId}${this.apiSignInUrl}`;
    const method = "PUT";
    const requestBody = { player_id: playerId };
    const warnMessage = "There was a problem voting for 'Player of the match'";
    const response = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      requestBody
    );

    return { playerId: response.data.player_id as string };
  };

  public deleteVoteForPlayerOfTheMatch = async (matchId: string) => {
    const url = `/football/potm/${matchId}${this.apiSignInUrl}`;
    const method = "DELETE";
    const warnMessage =
      "There was a problem with deleting vote for 'Player of the match'";
    await this.fetchWithAuth(url, warnMessage, method);
  };

  public getOwnVotesForPlayerOfTheMatch = async (
    filters: PotmUserVotesFilters
  ) => {
    let url = `/football/potm${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructFiltersUrl();
    }

    const warnMessage =
      "There was a problem fetching user's own votes for 'Player of the match'";
    const data = await this.fetchWithAuth(url, warnMessage);

    return this.playerOfTheMatchRemapper.remapUserVotes(data);
  };

  public getResultsForPlayerOfTheMatch = async (
    matchId: string,
    disableCache?: boolean
  ) => {
    const url = `/football/potm/${matchId}${this.apiSignInUrl}${
      isDisabledCache(disableCache) ? initDisableCache() : ""
    }`;
    const warnMessage =
      "There was a problem fetching results for 'Player of the match'";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.playerOfTheMatchRemapper.remapResults(data);
  };
}
